import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { BarChart3, Calendar, CreditCard, DollarSign, Eye, Globe, Layout, MapPin, MousePointer, Percent, RefreshCcw, TrendingDown, TrendingUp, User } from 'lucide-react';

const EditAnalyticsForm = ({ data, onClose, onSubmit }) => {
  debugger
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    value: '',
    adExchangeImpressions: 0,
    adExchangeClicks: 0,
    adExchangeCTR: 0,
    adExchangeRevenue: 0,
    adExchangeAvgECPM: 0,
    adExchangeAdRequests: 0,
    adExchangeMatchRate: 0,
    adExchangeFillRate: 0,
    adExchangePayout: 0,
    date: new Date(),
  });
  const [loading, setLoading] = useState(false);
  const [revenuePayout, setrevenuePayout] = useState(data.adExchangePayout || 0);

  useEffect(() => {
    if (data) {
      setFormData({
        ...data,
        date: data.date ? new Date(data.date) : new Date(),
      });
    }
  }, [data]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (e.target.name == "adExchangeRevenue") {
      const revenue = e.target.value;
      const payoutDeduction = revenue * 15 / 100; // 15% of revenue
      const payout = revenue - payoutDeduction;
      setrevenuePayout(payout);
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({
      ...prev,
      date
    }));
  };


  const handleSubmit = (e) => {
    setLoading(true);
    e.preventDefault();
    formData.adExchangePayout = revenuePayout;
    onSubmit(formData);
    setLoading(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm z-50 transition-opacity duration-300">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-4xl max-h-[90vh] overflow-y-auto border border-gray-100">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Edit Analytics
            </h2>
            <p className="text-gray-500 mt-1">Update the analytics data below</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200 p-1 rounded-full hover:bg-gray-100"
            aria-label="Close modal"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Metrics Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { field: 'adExchangeImpressions', label: 'Impressions', icon: <Eye className="h-5 w-5 text-gray-400" /> },
              { field: 'adExchangeClicks', label: 'Clicks', icon: <MousePointer className="h-5 w-5 text-gray-400" /> },
              { field: 'adExchangeCTR', label: 'CTR (%)', icon: <Percent className="h-5 w-5 text-gray-400" /> },
              { field: 'adExchangeRevenue', label: 'Revenue ($)', icon: <DollarSign className="h-5 w-5 text-gray-400" /> },
              { field: 'adExchangeAvgECPM', label: 'Avg eCPM ($)', icon: <BarChart3 className="h-5 w-5 text-gray-400" /> },
              { field: 'adExchangeAdRequests', label: 'Ad Requests', icon: <RefreshCcw className="h-5 w-5 text-gray-400" /> },
              { field: 'adExchangeMatchRate', label: 'Match Rate (%)', icon: <TrendingUp className="h-5 w-5 text-gray-400" /> },
              { field: 'adExchangeFillRate', label: 'Fill Rate (%)', icon: <TrendingDown className="h-5 w-5 text-gray-400" /> }
            ].map(({ field, label, icon }) => (
              <div key={field} className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">{label}</label>
                <div className="relative">
                  <input
                    name={field}
                    type="number"
                    value={formData[field]}
                    onChange={handleInputChange}
                    className="block w-full pl-10 pr-3 py-2.5 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 rounded-lg shadow-sm transition-all"
                    required
                    step={field.includes('Rate') || field.includes('CTR') ? "0.01" : "1"}
                  />
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    {icon}
                  </div>
                </div>
              </div>
            ))}

            {/* Payout (read-only) */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Payout ($)</label>
              <div className="relative">
                <input
                  name="adExchangePayout"
                  readOnly
                  type="number"
                  value={revenuePayout}
                  className="block w-full pl-10 pr-3 py-2.5 text-base border border-gray-300 bg-gray-50 rounded-lg shadow-sm"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <CreditCard className="h-5 w-5 text-gray-400" />
                </div>
              </div>
            </div>

            {/* Date Picker */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Date</label>
              <div className="relative">
                <DatePicker
                  selected={formData.date}
                  onChange={handleDateChange}
                  className="block w-full pl-10 pr-3 py-2.5 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 rounded-lg shadow-sm transition-all"
                  required
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Calendar className="h-5 w-5 text-gray-400" />
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </span>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditAnalyticsForm;
